import { useLayoutEffect, useRef, useState } from "react";

interface DescriptionWithShowMoreProps {
  description: string;
  className?: string;
  lines?: number;
}

const DescriptionWithShowMore = ({
  description,
  className = "text-muted fs-6 mb-0 lh-normal",
  lines = 2,
}: DescriptionWithShowMoreProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [visibleCharCount, setVisibleCharCount] = useState<number>(
    description?.length || 0
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const dummyRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const dummy = dummyRef.current;
    if (!container || !dummy || !description) return;

    const measureAndCalculate = () => {
      const computedStyle = window.getComputedStyle(container);
      let lh = parseFloat(computedStyle.lineHeight);
      if (isNaN(lh) || lh === 0) {
        lh = parseFloat(computedStyle.fontSize) * 1.4;
      }
      const maxHeight = lh * lines;

      // Check if full text fits within allowed lines
      dummy.textContent = description;
      if (dummy.offsetHeight <= maxHeight + 1.5) {
        setIsOverflowing(false);
        setVisibleCharCount(description.length);
        return;
      }

      setIsOverflowing(true);

      // Binary search for exact character cutoff that fits on `lines` with "... Show More"
      let low = 0;
      let high = description.length;
      let best = 0;
      const suffix = "... Show More";

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        dummy.textContent = description.slice(0, mid) + suffix;
        if (dummy.offsetHeight <= maxHeight + 1.5) {
          best = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      setVisibleCharCount(best);
    };

    measureAndCalculate();

    let animationFrameId: number;
    const resizeObserver = new ResizeObserver(() => {
      animationFrameId = window.requestAnimationFrame(() => {
        measureAndCalculate();
      });
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [description, lines]);

  if (!description) return null;

  const truncatedText =
    !isExpanded && isOverflowing
      ? description.slice(0, visibleCharCount).trimEnd() + "... "
      : description;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: "relative", wordBreak: "break-word" }}
    >
      {/* Hidden dummy element for exact layout measurement */}
      <div
        ref={dummyRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          visibility: "hidden",
          pointerEvents: "none",
          zIndex: -1,
          wordBreak: "break-word",
        }}
      />

      <span>{truncatedText}</span>

      {isOverflowing && (
        <span
          className="fs-7 cursor-pointer fw-semibold ms-1"
          style={{ cursor: "pointer", whiteSpace: "nowrap", color: "#000000" ,textDecoration:"underline"}}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
        >
          {isExpanded ? "Show Less" : "Show More"}
        </span>
      )}
    </div>
  );
};

export default DescriptionWithShowMore;